import { useMutation } from "@apollo/client";
import { useEffect } from "react";
import { CHAT_PRESENCE_HEARTBEAT } from "gql/Conversation/mutations";
import { useUserStore } from "stores/user/userStore";

/**
 * Pings the backend periodically so it can mark the current user as "online"
 * for chat presence purposes (used by the unread-messages digest email to
 * skip users that are currently active).
 *
 * - Sends one heartbeat on mount.
 * - Re-pings every 30s while the tab is visible.
 * - Pauses while the tab is hidden; pings immediately on visibility return.
 */
export const useChatPresenceHeartbeat = () => {
  const user = useUserStore((s) => s.user);
  const [heartbeat] = useMutation(CHAT_PRESENCE_HEARTBEAT);

  useEffect(() => {
    if (!user?.id) return;

    const ping = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      heartbeat().catch(() => {});
    };

    ping();
    const interval = setInterval(ping, 30000);

    const onVis = () => {
      if (document.visibilityState === "visible") ping();
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [user?.id, heartbeat]);
};
